import { useRef, type MouseEvent, type PointerEvent } from 'react'

type PullHandleProps = {
  onActivate: () => void
  direction: 'up' | 'down'
  label: string
  title: string
  className?: string
}

/** Activates a handle after a deliberate directional pull, tap or keyboard activation. */
export default function PullHandle({
  onActivate,
  direction,
  label,
  title,
  className = '',
}: PullHandleProps) {
  const gestureStart = useRef<{ pointerId: number; x: number; y: number } | null>(null)
  const suppressClick = useRef(false)

  /** Captures the pointer so releasing outside the handle still completes the gesture. */
  function handlePointerDown(event: PointerEvent<HTMLButtonElement>) {
    if (event.button !== 0 || gestureStart.current) return
    suppressClick.current = false
    gestureStart.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  /** Activates only after a pull of at least 32 pixels in the configured direction. */
  function handlePointerUp(event: PointerEvent<HTMLButtonElement>) {
    const start = gestureStart.current
    if (!start || start.pointerId !== event.pointerId) return
    gestureStart.current = null
    const horizontalDistance = Math.abs(event.clientX - start.x)
    const verticalDistance =
      direction === 'up' ? start.y - event.clientY : event.clientY - start.y
    // Browsers also dispatch click after a drag; do not let that activate the handle accidentally.
    suppressClick.current = Math.max(horizontalDistance, Math.abs(verticalDistance)) > 8
    if (verticalDistance >= 32 && verticalDistance > horizontalDistance) {
      onActivate()
    }
  }

  /** Discards interrupted gestures without activating the handle. */
  function handlePointerCancel() {
    gestureStart.current = null
    suppressClick.current = true
  }

  /** Preserves tap and native button keyboard activation while ignoring drag-generated clicks. */
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    if (!suppressClick.current || event.detail === 0) onActivate()
    suppressClick.current = false
  }

  return (
    <button
      type="button"
      aria-label={label}
      title={title}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onLostPointerCapture={() => {
        gestureStart.current = null
      }}
      onClick={handleClick}
      className={`flex h-6 w-16 shrink-0 touch-none items-center justify-center rounded-full cursor-grab active:cursor-grabbing focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${className}`}
    >
      <span
        aria-hidden="true"
        className="w-8 h-1 rounded-full bg-slate-300 dark:bg-slate-600/50"
      />
    </button>
  )
}
